import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from 'axios';
import { Button, Table, message, Form, Modal, Select, Spin, Space, Row, Col, Tooltip, Tag, Input, Card, Typography, Avatar } from 'antd';
import { Link, useNavigate } from "react-router-dom";
import { AiFillEdit } from "react-icons/ai";
import { SettingOutlined, EditOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { IoSettingsSharp, IoReload } from "react-icons/io5";
import {useTranslation} from "react-i18next";

const { Option } = Select;
const { Title, Text } = Typography;
const EditableContext = React.createContext(null);

export const Axios = axios.create({
    baseURL: process.env.REACT_APP_APPI_ADDRESS
});

const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

const EditableCell = ({
                          title,
                          editable,
                          children,
                          dataIndex,
                          record,
                          handleSave,
                          ...restProps
                      }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);

    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{ margin: 0 }}
                name={dataIndex}
                rules={[
                    {
                        required: true,
                        message: `${title} es requerido.`,
                    },
                ]}
            >
                <Input
                    ref={inputRef}
                    onPressEnter={save}
                    onBlur={save}
                    style={{
                        borderRadius: '6px',
                        border: '2px solid #253289',
                        boxShadow: '0 2px 8px rgba(37, 50, 137, 0.2)'
                    }}
                    placeholder="Ingrese el valor"
                />
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{
                    paddingRight: 24,
                    cursor: 'pointer',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                }}
                onClick={toggleEdit}
                onMouseEnter={(e) => {
                    e.target.style.background = '#f0f6ff';
                    e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.transform = 'scale(1)';
                }}
                title="Haga click para editar"
            >
                {children}
                <EditOutlined
                    style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        fontSize: '10px',
                        color: '#999',
                        opacity: 0.7
                    }}
                />
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

const ConfVariables = () => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState([]);
    const [spinning, setSpinning] = useState(false);
    const [spinTitle, setSpinTitle] = useState(t('global.loading'));
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();

    const headers = {
        'accept': '*/*',
        'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
        'Content-Type': 'application/json',
    };

    useEffect(() => {
        recargar();
    }, []);

    const recargar = () => {
        setSpinTitle(t('global.loading'));
        setSpinning(true);
        setSelectedRowKeys([]);
        let aux = [];

        Axios.get(`sd/getConfVariables`, { headers: headers })
            .then(response => {
                setSpinning(false);
                if (response.data.data.length > 0) {
                    for (let i = 0; i < response.data.data.length; i++) {
                        aux.push({
                            key: response.data.data[i].idconfvariables,
                            idconfvariables: response.data.data[i].idconfvariables,
                            tiempoentrega: response.data.data[i].tiempoentrega,
                            factorestandar: response.data.data[i].factorestandar,
                            factorexpress: response.data.data[i].factorexpress,
                            latitude: response.data.data[i].latitude,
                            longitude: response.data.data[i].longitude
                        });
                    }
                }
                setData(aux);
            })
            .catch(error => {
                setSpinning(false);
            });
    };

    const handleSave = (row) => {
        const newData = [...data];
        const index = newData.findIndex(item => row.key === item.key);
        if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, {
                ...item,
                ...row,
            });
            setData(newData);
            saveData(newData[index]);
        } else {
            newData.push(row);
            setData(newData);
            saveData(row);
        }
    };

    const saveData = (row) => {
        Axios.post("sd/updateVariablesConf", {
            idconfvariables: row.idconfvariables,
            tiempoentrega: row.tiempoentrega,
            factorestandar: row.factorestandar,
            factorexpress: row.factorexpress,
            latitude: row.latitude,
            longitude: row.longitude,
        }, {
            headers: headers
        }).then(response => {
            messageApi.success('Datos actualizados correctamente');
        }).catch(error => {
            messageApi.error('Error al actualizar los datos');
        });
    };

    const columns = [
        {
            title: <p style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
                color: "#2c3e50",
                fontFamily: "'Roboto', sans-serif",
                textShadow: "1px 1px 2px rgba(0,0,0,0.2)"
            }}>{t('variables.tiempo_entrega')}</p>,
            dataIndex: 'tiempoentrega',
            // width: 200,
            editable: true,
            render: (text) => (
                <Tag color='#834e56'>
                    {text.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: <p style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
                color: "#2c3e50",
                fontFamily: "'Roboto', sans-serif",
                textShadow: "1px 1px 2px rgba(0,0,0,0.2)"
            }}>{t('variables.factor_estandar')}</p>,
            dataIndex: 'factorestandar',
            editable: true,
            render: (text) => (
                <Tag color='#0ca678'>
                    {text.toUpperCase()}
                </Tag>
            ),
        },{
            title: <p style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
                color: "#2c3e50",
                fontFamily: "'Roboto', sans-serif",
                textShadow: "1px 1px 2px rgba(0,0,0,0.2)"
            }}>{t('variables.factor_express')}</p>,
            dataIndex: 'factorexpress',
            editable: true,
            render: (text) => (
                <Tag
                    color='#1890ff'
                    style={{
                        borderRadius: '8px',
                        padding: '4px 12px',
                        fontWeight: '500',
                        fontSize: '12px',
                        border: 'none'
                    }}
                >
                    ${text}
                </Tag>
            ),
        },{
            title: <p style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
                color: "#2c3e50",
                fontFamily: "'Roboto', sans-serif",
                textShadow: "1px 1px 2px rgba(0,0,0,0.2)"
            }}>{t('variables.latitude')}</p>,
            dataIndex: 'latitude',
            editable: true,
            render: (text) => (
                <Tag
                    style={{
                        background: '#722ed1',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '4px 12px',
                        fontWeight: '500',
                        fontSize: '12px',
                        border: 'none'
                    }}
                >
                    {text}°
                </Tag>
            ),
        },{
            title: <p style={{
                margin: "0px 0",
                fontSize: "15px",
                fontWeight: "bold",
                color: "#2c3e50",
                fontFamily: "'Roboto', sans-serif",
                textShadow: "1px 1px 2px rgba(0,0,0,0.2)"
            }}>{t('variables.longitude')}</p>,
            dataIndex: 'longitude',
            editable: true,
            render: (text) => (
                <Tag
                    color='#eb2f96'
                    style={{
                        borderRadius: '8px',
                        padding: '4px 12px',
                        fontWeight: '500',
                        fontSize: '12px',
                        border: 'none'
                    }}
                >
                    {text}°
                </Tag>
            ),
        }
    ];

    const mergedColumns = columns.map(col => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: record => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave: handleSave,
            }),
        };
    });

    // const onSelectChange = (newSelectedRowKeys) => {
    //     setSelectedRowKeys(newSelectedRowKeys);
    // };
    //
    // const rowSelection = {
    //     selectedRowKeys,
    //     onChange: onSelectChange,
    // };

    return (
        <div style={{
            minHeight: "100vh",
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: "16px"
        }}>
            <Spin spinning={spinning} tip={spinTitle} size={"large"}>
                {/* Header Principal */}
                <div style={{
                    background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '12px',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                        }}>
                            <SettingOutlined />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Typography.Title level={4} style={{
                                color: 'white',
                                margin: 0,
                                fontSize: '16px'
                            }}>
                                {t('variables.title')}
                            </Typography.Title>
                            <Typography.Text style={{
                                color: 'rgba(255,255,255,0.8)',
                                margin: 0,
                                fontSize: '12px'
                            }}>
                                Configuración del sistema • {data.length} variables
                            </Typography.Text>
                        </div>
                        <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '8px',
                            padding: '8px 16px'
                        }}>
                            <Typography.Text style={{ color: 'white', margin: 0, fontSize: '14px', fontWeight: 'bold' }}>
                                {data.length} variables
                            </Typography.Text>
                        </div>
                    </div>
                </div>

                {contextHolder}

                {/* Panel de Herramientas */}
                <Card style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: 'none',
                    marginBottom: '12px'
                }} styles={{ body: { padding: '8px' } }}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title={t('global.reload')}>
                            <Button
                                type="primary"
                                icon={<IoReload/>}
                                onClick={recargar}
                                loading={spinning}
                                size="small"
                                style={{
                                    background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
                                    borderColor: '#253289',
                                    borderRadius: '8px',
                                    height: '28px',
                                    fontWeight: '500',
                                    fontSize: '12px',
                                    padding: '0 12px',
                                    boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)'
                                }}
                            >
                                {t('global.reload')}
                            </Button>
                        </Tooltip>
                    </div>
                </Card>

                {/* Tabla de Variables */}
                <Card style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: 'none'
                }} styles={{ body: { padding: '0' } }}>
                    <Table
                        size="small"
                        components={{
                            body: {
                                row: EditableRow,
                                cell: EditableCell,
                            },
                        }}
                        columns={mergedColumns}
                        dataSource={data}
                        pagination={false}
                        style={{
                            background: 'white',
                            borderRadius: '8px'
                        }}
                    />
                </Card>
            </Spin>
        </div>
    );
};

export default ConfVariables;
